import React from 'react';
import { HEATING_MEDIUM, TANK_COATING_TYPES, ventilationOptions, iceClassNames, imoAppNames, imoClassNames } from '../../../../../../constants/vesselRequirements';
import { formConfigUtils } from '../../../../../common/Form/formConfig.utils';
import { FormContextProvider, useForm } from '../../../../../common/Form/form';
import { FormInput, FormInputCol } from '../../../../../common/Form/FormInput';
import { LIQUID_TYPES_BY_PURPOSE } from '../../../../../../constants/vesselTypes';
import TypeByPurpose from '../../../../../Vessels/Single/Rows/TypeByPurpose';
import TypeBySpecialty from '../../../../../Vessels/Single/Rows/TypeBySpecialty';
import TypeByConstruction from '../../../../../Vessels/Single/Rows/TypeByConstruction';
import { RegisterClassMultiNoRedux } from '../../../../../Vessels/Single/Rows/RegisterClassMulti';
import { FormSelectFlags } from '../../../../../common/Form/FormSelectFlags';
import { FormGears, FormGrabs } from '../../../../../common/Form/FormGearsAndGrabs';
import { FormPumps } from '../../../../../common/Form/FormPumps';
import { FormIGS } from '../../../../../common/Form/FormIGS';

export const fixVesselRequirementsData = (vr) => {
    if (!vr || typeof vr !== 'object') return;

    if (vr.typeByPurpose) vr.typeByPurpose = vr.typeByPurpose.toUpperCase();
    if (vr.typeBySpecialty) vr.typeBySpecialty = vr.typeBySpecialty.toUpperCase();
    if (vr.typeByConstruction) vr.typeByConstruction = vr.typeByConstruction.toUpperCase();
};

const { validateMin, validateMax } = formConfigUtils;

const config = {
    dwtMin: {
        label: 'DWT min',
        type: 'number',
        validate: validateMin({ maxFieldName: 'dwtMax' }),
    },
    dwtMax: {
        label: 'DWT max',
        type: 'number',
        validate: validateMax({ minFieldName: 'dwtMin' }),
    },
    capacityMin: {
        label: 'Capacity min',
        type: 'number',
        validate: validateMin({ maxFieldName: 'capacityMax' }),
    },
    capacityMax: {
        label: 'Capacity max',
        type: 'number',
        validate: validateMax({ minFieldName: 'capacityMin' }),
    },
    typeByPurpose: {
        render: ({ form }) => (<TypeByPurpose
            type={form.values.typeByPurpose}
            changeField={form.onChange}
            fromTcOrder
        />),
    },
    typeBySpecialty: {
        render: ({ form }) => (
            <TypeBySpecialty
                typeBySpecialty={form.values.typeBySpecialty}
                changeField={form.onChange}
                typeByPurpose={form.values.typeByPurpose}
                fromTcOrder
            />
        ),
    },
    typeByConstruction: {
        render: ({ form }) => (
            <TypeByConstruction
                type={form.values.typeByConstruction}
                changeField={form.onChange}
            />
        ),
    },
    iceClass: {
        label: 'Ice Class',
        type: 'select',
        options: iceClassNames,
    },
    teuMin: {
        label: 'Teu min, m',
        type: 'number',
        validate: validateMin({
            maxFieldName: 'teuMax',
            min: 50,
            max: 30000,
        }),
        disabled: form => !['MPP', 'CONT'].includes(form.values.typeByPurpose),
    },
    teuMax: {
        label: 'Teu max, m',
        type: 'number',
        validate: validateMax({ minFieldName: 'teuMin', min: 50, max: 30000 }),
        disabled: form => form.config.teuMin.disabled(form),
    },
    linerMeterMin: {
        label: 'Liner meter min, m',
        type: 'number',
        validate: validateMin({ maxFieldName: 'linerMeterMax' }),
        disabled: form => !/ro-(ro|lo)/i.test(form.values.typeBySpecialty),
    },
    linerMeterMax: {
        label: 'Liner meter max, m',
        type: 'number',
        validate: validateMax({ maxFieldName: 'linerMeterMin' }),
        disabled: form => form.config.linerMeterMin.disabled(form),
    },
    loaMax: {
        label: 'LOA max, m',
        type: 'number',
    },
    beamMax: {
        label: 'Beam max, m',
        type: 'number',
    },
    draftMax: {
        label: 'Draft max, m',
        type: 'number',
    },
    piClubMember: {
        label: 'PI Club',
        type: 'checkbox',
    },
    ageMin: {
        label: 'Age min',
        type: 'number',
        validate: validateMin({ maxFieldName: 'ageMax' }),
    },
    ageMax: {
        label: 'Age max',
        type: 'number',
        validate: validateMax({ minFieldName: 'ageMin' }),
    },
    openHatch: {
        label: 'Open hatch',
        type: 'select-fitted',
    },
    boxShaped: {
        label: 'Box shaped',
        type: 'select-fitted',
    },
    registerClasses: {
        render: ({ form }) => (
            <RegisterClassMultiNoRedux
                tcOrder={{ vesselRequirements: { registerClasses: form.values.registerClasses || [] } }}
                updateTcOrder={(_, values) => {
                    form.onChange({
                        field: 'registerClasses',
                        value: values.vesselRequirements.registerClasses,
                    });
                }}
            />
        ),
    },
    flags: {
        render: FormSelectFlags,
    },
    gearV2: {
        render: FormGears,
    },
    grabV2: {
        render: FormGrabs,
    },
    pumps: {
        render: FormPumps,
    },
    ventilationFitted: {
        label: 'Ventilation',
        type: 'select-fitted',
        onChange: (value, form) => !value && form.onChange({ field: 'ventilationType', value: '' }),
    },
    ventilationType: {
        label: 'Ventilation',
        type: 'select',
        options: ventilationOptions,
        disabled: form => !form.values.ventilationFitted,
    },
    referPlugFitted: {
        label: 'Reefer plug',
        type: 'select-fitted',
        onChange: (value, form) => !value && form.onChange([
            { field: 'referPlugQuantity', value: null },
        ]),
    },
    referPlugQuantity: {
        label: 'Reefer plugs quantity',
        type: 'number',
        disabled: form => !form.values.referPlugFitted,
    },
    imoFitted: {
        label: 'Imo',
        type: 'select-fitted',
        onChange: (value, form) => !value && form.onChange([
            { field: 'imoApp', value: null },
            { field: 'imoClass', value: null },
        ]),
    },
    imoApp: {
        label: 'Imo app',
        type: 'select',
        options: imoAppNames,
        disabled: form => !form.values.imoFitted,
        onChange: (_, form) => form.onChange({ field: 'imoFitted', value: true }),
    },
    imoClass: {
        label: 'Imo class',
        type: 'select',
        options: imoClassNames,
        disabled: form => !form.values.imoFitted,
        onChange: (_, form) => form.onChange({ field: 'imoFitted', value: true }),
    },
    scrubberFitted: {
        label: 'Scrubber',
        type: 'select-fitted',
    },
    co2Fitted: {
        label: 'CO2Fitted',
        type: 'select-fitted',
    },
    a60BulkheadFitted: {
        label: 'A60 bulkhead',
        type: 'select-fitted',
    },
    itfFitted: {
        label: 'Itf',
        type: 'select-fitted',
    },
    cementHolesFitted: {
        label: 'Cement holes',
        type: 'select-fitted',
    },
    lakesFitted: {
        label: 'Lakes',
        type: 'select-fitted',
    },
    logsFitted: {
        label: 'Logs',
        type: 'select-fitted',
    },
    aussieFitted: {
        label: 'Aussie',
        type: 'select-fitted',
    },
    strengthenHeavy: {
        label: 'Strengthened heavy',
        type: 'select-fitted',
    },
    wholeTank: {
        label: 'Whole Tank',
        type: 'checkbox',
    },
    coatingType: {
        label: 'Coating Type',
        type: 'select',
        options: ['', ...TANK_COATING_TYPES],
    },
    'heating.exchanger': {
        label: 'Heat Exchanger',
        type: 'select-fitted',
        onChange(value, form) {
            !value && form.onChange(['heating.medium', 'heating.maxLoaded', 'heating.maxMaintained'].map(field => ({ field, value: null })));
        },
    },
    'heating.medium': {
        label: 'Heating medium',
        type: 'select',
        options: ['', ...HEATING_MEDIUM],
        disabled: form => !form.values['heating.exchanger'],
    },
    'heating.maxLoaded': {
        label: 'Max loaded °C',
        type: 'number',
        disabled: form => !form.values['heating.exchanger'],
    },
    'heating.maxMaintained': {
        label: 'Max maintained °C',
        type: 'number',
        disabled: form => !form.values['heating.exchanger'],
    },
    newPanama: {
        label: 'New Panama',
        type: 'select-fitted',
    },
    bwts: {
        label: 'BWTS',
        hint: 'Ballast water treatment system',
        type: 'select-fitted',
    },
    coiled: {
        label: 'Coiled',
        type: 'select-fitted',
    },
    cargoTankDryer: {
        label: 'Cargo tank dryer',
        type: 'select-fitted',
    },
    cargoCoolingSystem: {
        label: 'Cargo cooling system',
        type: 'select-fitted',
    },
    crudeOilWashing: {
        label: 'Crude oil washing',
        type: 'select-fitted',
    },
    rcttms: {
        label: 'RCTTMS',
        hint: 'Remote cargo tank temperature monitoring system',
        type: 'select-fitted',
    },
    rctpms: {
        label: 'RCTPMS',
        hint: 'Remote cargo tank pressure monitoring system',
        type: 'select-fitted',
    },
    bowChainStopper: {
        label: 'Bow chain stopper',
        type: 'select-fitted',
    },
    chainSize: {
        label: 'Chain size, mm',
        type: 'number',
        disabled: form => !form.values.bowChainStopper,
    },
    ngs: {
        label: 'NGS',
        type: 'select-fitted',
    },
    ngsCapacity: {
        label: 'Capacity, cbm/hr (95%)',
        type: 'number',
        disabled: form => !form.values.ngs,
    },
    vrs: {
        label: 'VRS',
        type: 'select-fitted',
    },
    vrsNumLines: {
        label: 'Num of independent lines',
        type: 'number',
        disabled: form => !form.values.vrs,
    },
    bowThruster: {
        label: 'Bow thruster',
        type: 'select-fitted',
    },
    bowThrusterNum: {
        label: 'Num, pc',
        type: 'number',
        disabled: form => !form.values.bowThruster,
    },
    bowThrusterBhp: {
        label: 'BHP',
        type: 'number',
        disabled: form => !form.values.bowThruster,
    },
    bowThrusterKw: {
        label: 'kW',
        type: 'number',
        disabled: form => !form.values.bowThruster,
    },
    stemThruster: {
        label: 'Stem thruster',
        type: 'select-fitted',
    },
    stemThrusterNum: {
        label: 'Num, pc',
        type: 'number',
        disabled: form => !form.values.stemThruster,
    },
    stemThrusterBhp: {
        label: 'BHP',
        type: 'number',
        disabled: form => !form.values.stemThruster,
    },
    stemThrusterKw: {
        label: 'kW',
        type: 'number',
        disabled: form => !form.values.stemThruster,
    },
    igsFitted: {
        label: 'IGS',
        type: 'select-fitted',
    },
    igs: {
        render: FormIGS,
    },
};

const deleteObjectNulls = (obj) => {
    // eslint-disable-next-line no-restricted-syntax
    for (const key in obj) {
        if (obj[key] === null) {
            delete obj[key];
        }
    }
    return obj;
};

export function VesselRequirements({ onChange }) {
    const form = useForm({
        config,
        initialState: {},
        onChange: (data) => {
            onChange({
                vesselRequirements: {
                    ...data,
                    gearV2: data.gearV2 && {
                        ...data.gearV2,
                        summary: data.gearV2.summary.filter(x => x.type).map(({ capacityMin, ...x }) => deleteObjectNulls({ ...x, capacity: capacityMin })),
                    },
                    grabV2: data.grabV2 && {
                        ...data.grabV2,
                        summary: data.grabV2.summary.filter(x => x.type).map(({ volumeMin, ...x }) => deleteObjectNulls({ ...x, volume: volumeMin })),
                    },
                    registerClasses: data.registerClasses && data.registerClasses.map(x => ({ ...x, registerClass: x._id })),
                    flags: data.flags && data.flags.map(x => ({ ...x, flag: x._id })),

                    // turn the flat 'key.values' into a nested key: { value1: '...', value2: '...' }
                    'heating.exchanger': undefined,
                    'heating.medium': undefined,
                    'heating.maxLoaded': undefined,
                    'heating.maxMaintained': undefined,
                    heating: data['heating.exchanger'] ? {
                        exchanger: true,
                        medium: data['heating.medium'],
                        maxLoaded: data['heating.maxLoaded'],
                        maxMaintained: data['heating.maxMaintained'],
                    } : undefined,
                },
            });
        },
    });

    const isDryCargo = !LIQUID_TYPES_BY_PURPOSE.includes(form.values.typeByPurpose);

    return (
        <div className='paper-block margin-bottom'>
            <span className='name-block static huge-name'>Vessel Requirements</span>
            <FormContextProvider value={form}>
                <div className="row">
                    <FormInputCol name="dwtMin" />
                    <FormInputCol name="dwtMax" />
                    <FormInputCol name="capacityMin" />
                    <FormInputCol name="capacityMax" />
                    <FormInputCol name="typeByPurpose" />
                    <FormInputCol name="typeBySpecialty" />
                    {isDryCargo && <FormInputCol name="typeByConstruction" />}
                    <FormInputCol name="iceClass" />

                    {isDryCargo && (
                        <React.Fragment>
                            <FormInputCol name="teuMin" />
                            <FormInputCol name="teuMax" />
                            <FormInputCol name="linerMeterMin" />
                            <FormInputCol name="linerMeterMax" />
                        </React.Fragment>
                    )}

                    <FormInputCol name="loaMax" />
                    <FormInputCol name="beamMax" />
                    <FormInputCol name="draftMax" />

                    {isDryCargo && (<FormInputCol name="piClubMember" />)}
                    <FormInputCol name="ageMin" />
                    <FormInputCol name="ageMax" />
                    {!isDryCargo && (<FormInputCol name="piClubMember" />)}

                    {isDryCargo && (
                        <React.Fragment>
                            <FormInputCol name="openHatch" />
                            <FormInputCol name="boxShaped" />
                        </React.Fragment>
                    )}
                </div>

                <FormInput name="registerClasses" />
                <FormInput name="flags" />

                {isDryCargo && (
                    <React.Fragment>
                        <FormInput name="gearV2" />
                        <FormInput name="grabV2" />
                    </React.Fragment>
                )}

                {!isDryCargo && (
                    <React.Fragment>
                        <br />
                        <br />
                        <span className="name-block static huge-name">Pumps</span>
                        <FormInput name="pumps" />

                        <br />
                        <br />
                        <span className="name-block static huge-name">Tank Coating</span>
                        <div className="row">
                            <FormInputCol name="wholeTank" />
                            <FormInputCol name="coatingType" />
                        </div>

                        <br />
                        <br />
                        <span className="name-block static huge-name">Heating</span>
                        <div className="row">
                            <FormInputCol name="heating.exchanger" />
                            <FormInputCol name="heating.medium" />
                            <FormInputCol name="heating.maxLoaded" />
                            <FormInputCol name="heating.maxMaintained" />
                        </div>
                    </React.Fragment>
                )}

                <br />
                <br />
                <span className="name-block static huge-name">Technical equipment / certificate</span>

                <div className="row">
                    <FormInputCol name="ventilationFitted" />
                    <FormInputCol name="ventilationType" />

                    {isDryCargo && (
                        <React.Fragment>
                            <FormInputCol name="referPlugFitted" />
                            <FormInputCol name="referPlugQuantity" />
                        </React.Fragment>
                    )}

                    <FormInputCol name="imoFitted" />
                    <FormInputCol name="imoApp" />
                    <FormInputCol name="imoClass" />
                    <FormInputCol name="scrubberFitted" />

                    {isDryCargo && (
                        <React.Fragment>
                            <FormInputCol name="co2Fitted" />
                            <FormInputCol name="a60BulkheadFitted" />
                        </React.Fragment>
                    )}

                    <FormInputCol name="itfFitted" />

                    {isDryCargo && <FormInputCol name="cementHolesFitted" />}

                    <FormInputCol name="lakesFitted" />
                    
                    {isDryCargo && (
                        <React.Fragment>
                            <FormInputCol name="logsFitted" />
                            <FormInputCol name="aussieFitted" />
                            <FormInputCol name="strengthenHeavy" />
                        </React.Fragment>
                    )}

                    {!isDryCargo && (
                        <React.Fragment>
                            <FormInputCol name="newPanama" />
                            <FormInputCol name="bwts" />
                            <FormInputCol name="coiled" />
                            <FormInputCol name="cargoTankDryer" />
                            <FormInputCol name="cargoCoolingSystem" />
                            <FormInputCol name="crudeOilWashing" />
                            <FormInputCol name="rcttms" />
                            <FormInputCol name="rctpms" />
                            <FormInputCol name="bowChainStopper" />
                            <FormInputCol name="chainSize" />
                            <div className="col-xs-6" />
                            <FormInputCol name="ngs" />
                            <FormInputCol name="ngsCapacity" />
                            <FormInputCol name="vrs" />
                            <FormInputCol name="vrsNumLines" />
                            <FormInputCol name="bowThruster" />
                            <FormInputCol name="bowThrusterNum" />
                            <FormInputCol name="bowThrusterBhp" />
                            <FormInputCol name="bowThrusterKw" />
                            <FormInputCol name="stemThruster" />
                            <FormInputCol name="stemThrusterNum" />
                            <FormInputCol name="stemThrusterBhp" />
                            <FormInputCol name="stemThrusterKw" />
                        </React.Fragment>
                    )}
                </div>

                {!isDryCargo && (<FormInput name="igs" />)}
            </FormContextProvider>
        </div>
    );
}



// WEBPACK FOOTER //
// ./app/components/Letters/SingleNew/Cargo/Sidebar/Order/VesselRequirements.jsx